import React, { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export interface ImpersonatedUser {
  id: number;
  name: string;
  role: "student" | "tutor" | "organization";
  /** The dashboard path to navigate to */
  targetPath: string;
}

interface ImpersonationContextValue {
  impersonation: ImpersonatedUser | null;
  isImpersonating: boolean;
  startImpersonation: (user: ImpersonatedUser) => void;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue>({
  impersonation: null,
  isImpersonating: false,
  startImpersonation: () => {},
  stopImpersonation: () => {},
});

export const useImpersonation = () => useContext(ImpersonationContext);

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [impersonation, setImpersonation] = useState<ImpersonatedUser | null>(null);
  const navigate = useNavigate();

  const startImpersonation = useCallback((user: ImpersonatedUser) => {
    setImpersonation(user);
    navigate(user.targetPath);
  }, [navigate]);

  const stopImpersonation = useCallback(() => {
    setImpersonation(null);
    navigate("/admin/dashboard");
  }, [navigate]);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonation,
        isImpersonating: impersonation !== null,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};
